<template>
  <v-col cols="12">
    <v-card>
      <v-card-title class="text-h5"> Insider Stock Screener </v-card-title>

      <v-card-text>
        <!-- Options -->
        <v-row mt-6>
          <v-col cols="12" md="6" lg="6" xl="4" mb-4>
            <helpers-section-sub-header
              >Total Insider Ownership Percentage:</helpers-section-sub-header
            >
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="criteria.minimum_percent_ownership"
                  variant="outlined"
                  type="number"
                  label="From"
                  hide-details
                  class="pt-0"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="criteria.maximum_percent_ownership"
                  variant="outlined"
                  type="number"
                  label="To"
                  hide-details
                  class="pt-0"
                />
              </v-col>
            </v-row>
          </v-col>

          <!-- Avg. Daily Volume -->
          <v-col v-if="1 == 2" cols="12" md="6" lg="6" xl="4" mb-4>
            <helpers-section-sub-header>Avg. daily volume:</helpers-section-sub-header>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="criteria.avg_daily_volume_from"
                  type="number"
                  variant="outlined"
                  label="From"
                  hide-details
                  class="pt-0"
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="criteria.avg_daily_volume_to"
                  type="number"
                  variant="outlined"
                  label="To"
                  hide-details
                  class="pt-0"
                />
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="12" pa-0 />
          <!-- New line -->

          <!-- Sectors -->
          <v-col cols="12" xl="4" mb-2>
            <helpers-section-sub-header class="mb-2">
              Limit to Certain Sectors:
            </helpers-section-sub-header>
            <v-combobox
              v-model="criteria.sectors"
              :items="sectorNames"
              placeholder="Select sectors"
              multiple
              variant="outlined"
              chips
              closable-chips
              clearable
              density="compact"
              hide-details
              class="pa-0"
            />
          </v-col>

          <!-- Filter by stock market cap -->
          <v-col cols="12" xl="4" mb-2>
            <helpers-section-sub-header class="mb-2">
              Filter by stock market cap:
            </helpers-section-sub-header>
            <v-select
              v-model="criteria.market_caps"
              :items="marketCaps"
              return-object
              variant="outlined"
              item-title="name"
              placeholder="Select market caps"
              multiple
              density="compact"
              chips
              closable-chips
              clearable
              hide-details
              class="pa-0"
            />
          </v-col>

          <v-col cols="12" mb-4>
            <helpers-section-sub-header>Specific Tickers:</helpers-section-sub-header>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="criteria.ticker_filter"
                  type="text"
                  label="From"
                  variant="outlined"
                  hide-details
                  class="pt-0"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-btn variant="outlined" @click="updateData()">Search</v-btn>
          </v-col>
        </v-row>

        <!-- Table -->
        <v-data-table-server
          v-model:options="pagination"
          v-model:sort-by="pagination.sortBy"
          :headers="tableHeaders"
          :items="tableItems"
          :items-length="totalItems"
          :items-per-page-options="[10, 25, 50, 100]"
          :loading="loading"
          :no-data-text="loading ? 'Loading...' : 'No results found.'"
          must-sort
          class="mt-6"
        >
          <template #item="{ item }">
            <tr>
              <td>
                <a :href="'/stock/' + item.permalink">{{ item.symbol }}</a>
              </td>
              <td>{{ item.name }}</td>
              <td>{{ item.sector }}</td>
              <td>{{ item.industry }}</td>
              <td>{{ human_number(item.market_cap) }}</td>
              <td>
                <v-btn
                  variant="outlined"
                  title="click to view transactions"
                  color="primary"
                  @click="show_transactions(item)"
                  >{{ percent(item.insider_ownership_percentage) }}</v-btn
                >
              </td>
              <td>{{ human_number(item.insider_ownership_shares) }}</td>
              <td>{{ human_number(item.shares_outstanding) }}</td>
            </tr>
          </template>
        </v-data-table-server>
        <v-card-subtitle
          >Insider Ownership % only looks at transactions for the past 2 years.</v-card-subtitle
        >
      </v-card-text>
    </v-card>
    <transaction-details-dialog ref="transactionDetailsDialog" />
  </v-col>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import api from '@/store/api'
import { mapFields } from '@/store/helpers'
import cloneDeep from 'lodash/cloneDeep'
import debounce from 'lodash/debounce'

// Mixins
import FormattersMixin from '@/mixins/formatters'
import TransactionDetailsDialog from '@/components/page/insider-dashboard/_TransactionDetailsDialog'

// Components

// Static data
import { defaultInsiderBacktesterClusterCriteria } from '@/data/constants'

export default {
  components: {
    TransactionDetailsDialog
  },
  mixins: [FormattersMixin],

  data() {
    return {
      insider_detail_dialog: false,
      results: null,
      criteria: {
        date_range: 1,
        owner_types: [5],
        industries: [],
        sectors: [],
        minimum_percent_ownership: '',
        maximum_percent_ownership: '',
        avg_daily_volume_from: '',
        avg_daily_volume_to: '',
        market_caps: [],
        minimum_transaction_count: '',
        avg_dollar_value_volume_min: '',
        avg_dollar_value_volume_max: '',
        ticker_filter: ''
      },
      loading: false,
      tableHeaders: [
        { title: 'Ticker', key: 'symbol' },
        { title: 'Name', key: 'name' },
        { title: 'Sector', key: 'sector' },
        { title: 'Industry', key: 'industry' },
        { title: 'Market Cap', key: 'market_cap' },
        { title: '% Insider Ownership', key: 'insider_ownership_percentage' },
        { title: 'Insider Shares Held', key: 'insider_ownership_shares' },
        { title: 'Shares Outstanding', key: 'shares_outstanding' }
      ],
      totalItems: 0,
      tableItems: [],
      pagination: {
        sortBy: [{ key: 'insider_ownership_percentage', order: 'desc' }],
        page: 1,
        itemsPerPage: 10
      },
      tenFilterTransactions: [
        { value: 1, text: 'Show All Transactions' },
        { value: 2, text: 'All Purchases' },
        { value: 3, text: 'All Sells' },
        { value: 4, text: 'Open Market Purchases' },
        { value: 5, text: 'Open Market Sales' },
        { value: 6, text: 'Option Exercises' }
      ],
      tenFilterDates: [
        { value: 0, text: 'Past Day' },
        { value: 1, text: 'Past 3 Days' },
        { value: 2, text: 'Past 7 Days' },
        { value: 6, text: 'Past 2 Weeks' },
        { value: 3, text: 'Past Month' },
        { value: 4, text: 'Past 3 Months' },
        { value: 5, text: 'Past 6 Months' },
        { value: 7, text: 'Past Year' }
      ],
      filterOwnerTypes: [
        { value: 1, text: '>10% Owner' },
        { value: 2, text: 'Director' },
        { value: 3, text: 'Officer' },
        { value: 5, text: 'C-Level Officer (CEO, COO, CFO, etc.)' },
        { value: 4, text: 'Other Owner' }
      ],
      marketCaps: [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billiion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$250 million - $2 billion', id: 7 },
        { name: 'Below $250 million', id: 8 }
      ]
    }
  },

  computed: {
    sectorNames() {
      return useWwStore().sectors.map(sector => sector.name)
    },
    params() {
      return {
        ...this.criteria,
        rows: this.pagination.itemsPerPage,
        page: this.pagination.page,
        sort: this.pagination.sortBy[0]?.key,
        order: this.pagination.sortBy[0]?.order || 'asc',
        ...this.filtersParamsObject
      }
    }
  },
  watch: {
    params: {
      deep: true,
      handler() {
        this.updateData()
      }
    }
  },

  mounted() {
    // useWwStore().loadSectors()
  },

  created() {
    // Init with defaults
    // this.clusterCriteria = cloneDeep(defaultInsiderBacktesterClusterCriteria)
  },

  methods: {
    show_transactions(item) {
      this.$refs.transactionDetailsDialog.showDetails(
        { stock_id: item.stock_id, current_only: true },
        {}
      )
    },
    updateData: debounce(function () {
      if (this.loading) return
      this.loading = true

      api
        .post('form4_insider_ownership', { params: this.params })
        .then(response => {
          this.results = response.data
          this.tableItems = response.data.rows
          this.totalItems = response.data.records
        })
        .catch(error => {
          useAppStore().showNotification({
            type: 'error',
            text: (error.response && error.response.data) || error.message
          })
        })
        .then(() => (this.loading = false))
    }, 750)
  }
}
</script>
